import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Table,
  Typography,
  ConfigProvider,
} from "antd";
import { Box } from "@mui/material";
import dayjs from "dayjs";
import PropTypes from "prop-types";

const MonthlyDialog = ({ rowData, colData, date }) => {
  function getDaysInMonth(date) {
    const year = date.year();
    const month = date.month() + 1; // month() returns 0-based month, so add 1
    return dayjs(`${year}-${month}-01`).daysInMonth();
  }

  const [tabColumns, setTabColumns] = useState([
    { title: "Date", dataIndex: "date", editable: true },
    ...colData,
  ]);
  const [tabRows, setTabRows] = useState([rowData]);

  useEffect(() => {
    setData(originData);
  }, [tabRows]);

  const originData = Array.from({ length: getDaysInMonth(date) }).map(
    (_, i) => ({
      key: i.toString(),
      date: `${i + 1}-${date.month() + 1}-${date.year()}`,
      parameter: tabRows[0].parameter,
      quantity: tabRows.quantity,
      unit: tabRows[0].unit,
      co2: typeof tabRows.quantity === "number" ? tabRows.quantity * 1 : 0,
      ch4: typeof tabRows.quantity === "number" ? tabRows.quantity * 27.9 : 0,
      n2o: typeof tabRows.quantity === "number" ? tabRows.quantity * 273 : 0,
      tco2e:
        typeof tabRows.quantity === "number"
          ? tabRows.quantity * 1 +
            tabRows.quantity * 27.9 +
            tabRows.quantity * 273
          : 0,
      quantity2: tabRows.quantity2,
      tco2e2:
        typeof tabRows.quantity === "number"
          ? tabRows.parameter === "Purchased Grid Electricity"
            ? tabRows.quantity * 0.729
            : 0
          : 0,
      tco2e3: tabRows.quantity,
    })
  );

  const EditableCell = ({
    editing,
    dataIndex,
    title,
    inputType,
    record,
    index,
    children,
    ...restProps
  }) => {
    const inputNode =
      inputType === "number" ? (
        <InputNumber
          style={{
            color: "var(--SecondaryText)",
            backgroundColor: "var(--SecondaryBackground)",
            // activeBorderColor: "var(--LK_BrandBlue_1)",
            // activeShadow: "0 0 0 0px rgba(5,145,255,0.1)",
          }}
        />
      ) : inputType === "number-disabled" ? (
        <InputNumber
          disabled
          style={{
            color: "var(--Searchtext_muted)",
            backgroundColor: "var(--SecondaryBackground)",
          }}
        />
      ) : inputType === "disabled" ? (
        <Input
          disabled
          style={{
            color: "var(--Searchtext_muted)",
            backgroundColor: "var(--SecondaryBackground)",
          }}
        />
      ) : (
        <Input
          style={{
            color: "var(--SecondaryText)",
            backgroundColor: "var(--SecondaryBackground)",
          }}
        />
      );
    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={dataIndex}
            style={{
              margin: 0,
            }}
            rules={[
              {
                required: true,
                message: `Please Input ${title}!`,
              },
            ]}
          >
            {inputNode}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  EditableCell.propTypes = {
    editing: PropTypes.bool.isRequired, // Ensure editing is a boolean and required
    dataIndex: PropTypes.string.isRequired, // Ensure dataIndex is a string and required
    title: PropTypes.string.isRequired, // Ensure title is a string and required
    inputType: PropTypes.string.isRequired, // Ensure inputType is a string and required
    record: PropTypes.object.isRequired, // Ensure record is an object and required
    index: PropTypes.number.isRequired, // Ensure index is a number and required
    children: PropTypes.node, // Ensure children can be any renderable node
  };

  const [form] = Form.useForm();
  const [data, setData] = useState(originData);
  const [editingKey, setEditingKey] = useState("");

  const isEditing = (record) => record.key === editingKey;
  const edit = (record) => {
    form.setFieldsValue({
      parameter: "",
      quantity: 0,
      unit: "",
      co2: 0,
      ch4: 0,
      n2o: 0,
      tco2e: 0,
      tco2e2: 0,
      quantity2: 0,
      tco2e3: 0,
      date: "",
      ...record,
    });
    setEditingKey(record.key);
  };
  const cancel = () => {
    setEditingKey("");
  };
  const save = async (key) => {
    try {
      const row = await form.validateFields();
      const newData = [...data];
      const index = newData.findIndex((item) => key === item.key);
      if (index > -1) {
        const item = newData[index];
        newData.splice(index, 1, {
          ...item,
          ...row,
        });
        newData[index].co2 = newData[index].quantity * 1;
        newData[index].ch4 = newData[index].quantity * 27.9;
        newData[index].n2o = newData[index].quantity * 273;
        newData[index].tco2e =
          newData[index].co2 + newData[index].ch4 + newData[index].n2o;
        newData[index].tco2e2 =
          newData[index].parameter === "Purchased Grid Electricity"
            ? newData[index].quantity * 0.729
            : 0;
        setData(newData);
        setEditingKey("");
      } else {
        newData.push(row);
        setData(newData);
        setEditingKey("");
      }
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };
  const columns = [
    ...tabColumns,
    {
      title: "operation",
      dataIndex: "operation",
      render: (_, record) => {
        const editable = isEditing(record);
        return editable ? (
          <span>
            <Typography.Link
              onClick={() => save(record.key)}
              style={{
                marginInlineEnd: 8,
                color: "var(--LK_BrandBlue_1)",
              }}
            >
              Save
            </Typography.Link>
            <Popconfirm
              icon={null}
              title="Cancel?"
              description="Are you sure?"
              onConfirm={cancel}
              style={{
                backgroundColor: "var(--SecondaryBackground)",
              }}
            >
              <a style={{ color: "var(--LK_BrandBlue_1)" }}>Cancel</a>
            </Popconfirm>
          </span>
        ) : (
          <Typography.Link
            disabled={editingKey !== ""}
            onClick={() => edit(record)}
            style={{
              color:
                editingKey !== ""
                  ? "var(--Searchtext_muted)"
                  : "var(--LK_BrandBlue_1)",
            }}
          >
            Edit
          </Typography.Link>
        );
      },
    },
  ];
  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType:
          col.dataIndex === "quantity"
            ? "number"
            : col.dataIndex === "co2" ||
              col.dataIndex === "ch4" ||
              col.dataIndex === "n2o" ||
              col.dataIndex === "tco2e" ||
              col.dataIndex === "tco2e2"
            ? "number-disabled"
            : col.dataIndex === "date"
            ? "disabled"
            : "text",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });
  return (
    <Box style={{ width: "100%", boxSizing: "border-box" }}>
      <Box
        style={{
          display: "flex",
          flexDirection: "row",
          backgroundColor: "var(--SecondaryBackground)",
          padding: "8px",
          width: "100%",
          boxSizing: "border-box",
          border: "1px solid var(--HighlightedText_Stroke)",
        }}
      >
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            boxSizing: "border-box",
            maxWidth: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <Form
              form={form}
              component={false}
              style={{
                width: "100%",
                boxSizing: "border-box",
                maxWidth: "100%",
              }}
            >
              <ConfigProvider
                theme={{
                  token: {
                    borderRadius: 0,
                    colorText: "var(--grey-variation-tablerowsLK)",
                    colorTextHeading: "var(--grey-variation-tablerowsLK)",
                    fontSizeIcon: 10,
                    lineHeight: 1.5,
                  },
                  components: {
                    Table: {
                      colorBgContainer: "var(--SecondaryBackground)",
                      borderColor: "var(--table-boarder-line)",
                      headerBg: "var(--SecondaryBackground)",
                      headerSplitColor: "var(--SecondaryBackground)",
                      rowHoverBg: "var(--primary-background-color-hover)",
                      stickyScrollBarBg:
                        "var(--primary-background-color),var(--secondary-background)",
                      stickyScrollBarBorderRadius: 0,
                      cellPaddingBlockSM: "7.05px",
                      cellPaddingInlineSM: "7.1px",
                      width: "100%",
                      maxWidth: "100%",
                      boxSizing: "border-box",
                    },
                  },
                }}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  maxWidth: "100%",
                }}
              >
                <Table
                  components={{
                    body: {
                      cell: EditableCell,
                    },
                  }}
                  //rowSelection={rowSelection}
                  bordered={false}
                  tableLayout="fixed"
                  size="small"
                  pagination={false}
                  style={{
                    marginLeft: "2px",
                    marginRight: "2px",
                    marginTop: 0,
                    marginBottom: 0,
                    width: "99%",
                    maxWidth: "100%",
                    boxSizing: "border-box",
                    tableLayout: "fixed",
                    backgroundColor: "var(--SecondaryBackground)",
                  }}
                  dataSource={data}
                  columns={mergedColumns}
                  rowClassName={() => "editable-row"}
                  scroll={{
                    y: "369px",
                  }}
                />
              </ConfigProvider>
            </Form>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

MonthlyDialog.propTypes = {
  rowData: PropTypes.array.isRequired, // Assuming rowData is an array
  colData: PropTypes.array.isRequired, // Assuming colData is an array
  date: PropTypes.string.isRequired, // Assuming date is a string
};
export default MonthlyDialog;
